import React, { useState, useEffect, useRef } from "react";
import { extractYouTubeVideoId } from "../../api/videoUrlCrypto";
import { courseTitleToSlug } from "../../api/courseSlug";
import CustomYouTubePlayer from "../learning/CustomYouTubePlayer";

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=600&q=80";
const DEFAULT_VIDEO =
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";

export default function CourseCard({ courseData, onSelectCourse }) {
  const {
    badge,
    title,
    desc1,
    desc2,
    instructor,
    rating,
    price,
    originalPrice,
    imageUrl,
    avatarUrl,
    videoUrl,
  } = courseData || {};

  const [slideIndex, setSlideIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const videoRef = useRef(null);

  const activeImage = imageUrl || DEFAULT_IMAGE;
  const activeVideo = videoUrl || DEFAULT_VIDEO;
  const isYouTubeVideo = Boolean(extractYouTubeVideoId(activeVideo));


  useEffect(() => {
    if (isHovered || isPlaying) return;

    const intervalId = setInterval(() => {
      setSlideIndex((prev) => (prev === 0 ? 1 : 0));
    }, 4500);

    return () => clearInterval(intervalId);
  }, [isHovered, isPlaying]);

  useEffect(() => {
    if (slideIndex === 0 && isYouTubeVideo) {
      setIsPlaying(false);
    }

    const video = videoRef.current;
    if (!video) return;

    if (slideIndex === 0 && !video.paused) {
      video.pause();
      setIsPlaying(false);
    }
  }, [isYouTubeVideo, slideIndex]);

  const togglePlayPause = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleEnroll = () => {
    if (onSelectCourse) {
      onSelectCourse(courseData);
      return;
    }

    if (!courseData?.id) return;
    const isVideoCourse = ['2', 'video'].includes(
      String(courseData.courseType || '').trim().toLowerCase(),
    ) || courseData.id === 'premiere-pro-mastery';
    const route = isVideoCourse ? '#video-editing-course' : '#course-details';
    const courseSlug = courseTitleToSlug(courseData.title);
    window.location.hash = `${route}?course=${encodeURIComponent(courseSlug)}`;
  };

  return (
    <div
      className="course-card-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div>
        <div className="course-thumbnail-area">
          {badge && <span className="course-badge-tag">{badge}</span>}

         
          <button
            className={`inner-slider-nav-btn prev-btn ${
              slideIndex === 0 ? "hidden-nav" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setSlideIndex(0);
            }}
            type="button"
            aria-label="Previous Slide"
          >
            ‹
          </button>
          <button
            className={`inner-slider-nav-btn next-btn ${
              slideIndex === 1 ? "hidden-nav" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setSlideIndex(1);
            }}
            type="button"
            aria-label="Next Slide"
          >
            ›
          </button>

          
          <div
            className="inner-carousel-track"
            style={{ transform: `translateX(-${slideIndex * 50}%)` }}
          >
            
            <div className="inner-slide-media">
              <img
                src={activeImage}
                alt={title || "Course Cover"}
                loading="eager"
              />
            </div>

            
            <div
              className="inner-slide-media"
              onClick={isYouTubeVideo ? undefined : togglePlayPause}
              style={{ cursor: "pointer", position: "relative" }}
            >
              {isYouTubeVideo ? (
                slideIndex === 1 && (
                  <CustomYouTubePlayer
                    videoUrl={activeVideo}
                    title={`${title || "Course"} preview`}
                    className="course-card-player"
                    autoplay={isPlaying}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                )
              ) : (
                <>
                  <video
                    ref={videoRef}
                    src={activeVideo}
                    poster={activeImage}
                    playsInline
                    loop
                    preload="metadata"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <div
                    className="inner-video-overlay-play"
                    style={{
                      opacity: isPlaying ? 0 : 1,
                      pointerEvents: "none",
                      transition: "opacity 0.25s ease",
                    }}
                  >
                    {isPlaying ? "⏸" : "▶"}
                  </div>
                </>
              )}
            </div>
          </div>

          
          <div className="inner-slider-indicator-dots">
            <span
              className={`indicator-dot ${slideIndex === 0 ? "active-dot" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setSlideIndex(0);
              }}
            />
            <span
              className={`indicator-dot ${slideIndex === 1 ? "active-dot" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setSlideIndex(1);
              }}
            />
          </div>
        </div>

        
        <div className="course-details-area">
          <h4 className="course-main-title">{title || "Course Title"}</h4>

          <div className="course-card-desc-group">
            {desc1 && (
              <p className="course-card-subdesc">
                <span className="desc-bullet">•</span> {desc1}
              </p>
            )}
            {desc2 && (
              <p className="course-card-subdesc">
                <span className="desc-bullet">•</span> {desc2}
              </p>
            )}
          </div>

          <div className="instructor-profile-row">
            <img
              src={
                avatarUrl ||
                "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&q=80"
              }
              alt={instructor || "Expert"}
              className="instructor-avatar"
            />
            <div className="instructor-meta-text">
              <span className="instructor-label">Instructor</span>
              <span className="instructor-name">{instructor || "Expert"}</span>
            </div>
          </div>
        </div>
      </div>

      
      <div>
        <div className="rating-and-pricing-line">
          <span className="star-rating-pill">★ {rating || "4.9"}</span>
          <div className="pricing-block">
            <span className="sale-price">₹{price || "1,999"}</span>
            <span className="original-strike-price">
              ₹{originalPrice || "4,999"}
            </span>
          </div>
        </div>
        <button className="execute-enroll-button" type="button" onClick={handleEnroll}>
          Join Now
        </button>
      </div>
    </div>
  );
}